﻿import controller from "../../../../Resources/Scripts/_Shared/controller";
import React, { useContext, useEffect, useState, useRef } from "react";
import ReactDOM from "react-dom";
import {
    WaitingListFlowContextProvider,
    WaitingListFlowContext,
} from "./WaitingListFlowContext";
import { facility, products } from "./Endpoints";
import { Error } from "../../../../Resources/Scripts/_Shared/elements/Error";
import WaitingListProductDisplay from "./steps/WaitingListProductDisplay";
import CreateAccount from "./steps/CreateAccount";
import AddVehicle from "./steps/AddVehicle";
import DocumentUpload from "./steps/DocumentUpload";
import OrderOverview from "./steps/OrderOverview";
import OrderConfirmation from "./steps/OrderConfirmation";
import Introduction from "./steps/Introduction";
import StepProgress from "./partials/StepProgress";
import { findPlaceById, isWaitingListAttachmentsEnabled } from "./util";

function waitingListFlowPage(context) {
    const appContainer = context;
    const appConfig = controller.getConfig(context);

    const App = () => {
        const { waitingListFlowContext, setWaitingListFlowContext } =
            useContext(WaitingListFlowContext);
        const [loading, setLoading] = useState(true);
        const [error, setError] = useState(null);
        const topOfContainerRef = useRef(null);
        const urlParams = new URLSearchParams(window.location.search);
        const pmcId = urlParams.get("pmcId");
        const selectedLang = document
            .querySelector("html")
            .getAttribute("lang");

        useEffect(() => {
            const fetchData = async () => {
                try {
                    const [facilityRes] = await Promise.all([
                        facility.getFacility({
                            bookingController:
                                waitingListFlowContext?.episerver
                                    ?.bookingController,
                            fId: urlParams.get("fId"),
                            lang:
                                document.documentElement.lang === "nb"
                                    ? "no"
                                    : document.documentElement.lang,
                        }),
                    ]);

                    if (facilityRes.status !== 200) {
                        throw new Error("Failed to load required data");
                    }
                    
                    const selectedProduct = findPlaceById(facilityRes.body, pmcId);

                    const country = document
                    .querySelector("html")
                    .getAttribute("country");

                    setWaitingListFlowContext({
                        ...waitingListFlowContext,
                        country,
                        facility: {
                            id: facilityRes.body.Uid,
                            name: facilityRes.body.FacilityName,
                            city:
                                facilityRes.body.Address?.City ||
                                facilityRes.body.City,
                        },
                        product: selectedProduct,
                    });
                } catch (err) {
                    console.log(err);
                    setError(
                        err.message || "Failed to initialize waiting list"
                    );
                } finally {
                    setLoading(false);
                }
            };

            fetchData();
        }, []);

        if (loading) {
            return (
                <div className="waitinglistflowpage__loader">
                    <div className="aimo-spinner">
                        <span></span>
                        <span></span>
                        <span></span>
                        <span></span>
                    </div>
                </div>
            );
        }

        if (error) {
            return (
                <div className="waitinglistflowpage__error">
                    <div className="waitinglistflowpage__error__inner">
                        <h2>
                            {
                                appConfig.localization.global.errorMessages.load
                                    .heading
                            }
                        </h2>
                        <p>
                            {
                                appConfig.localization.global.errorMessages.load
                                    .text
                            }
                        </p>
                        <button onClick={() => window.location.reload()}>
                            {
                                appConfig.localization.global.errorMessages.load
                                    .button
                            }
                        </button>
                    </div>
                </div>
            );
        }

        const progressStepIndexes = [
            waitingListFlowContext.paymentTerm.index,
            waitingListFlowContext.vehicle.index,
            ...(isWaitingListAttachmentsEnabled(waitingListFlowContext)
                ? [waitingListFlowContext.document.index]
                : []),
            waitingListFlowContext.overview.index,
        ];

        const currentProgressStep =
            progressStepIndexes.indexOf(waitingListFlowContext.activeStep) + 1;

        const totalProgressSteps = progressStepIndexes.length;

        return (
            <div ref={topOfContainerRef}>
                {currentProgressStep > 0 &&
                    currentProgressStep != 5 &&
                    currentProgressStep != 0 && (
                        <StepProgress
                            currentStep={currentProgressStep}
                            totalSteps={totalProgressSteps}
                        />
                    )}
                {waitingListFlowContext.activeStep === waitingListFlowContext.introduction.index && <Introduction />}
                {waitingListFlowContext.activeStep === waitingListFlowContext.paymentTerm.index && (
                    <WaitingListProductDisplay />
                )}
                {waitingListFlowContext.activeStep === waitingListFlowContext.createAccount.index && <CreateAccount />}
                {waitingListFlowContext.activeStep === waitingListFlowContext.vehicle.index && <AddVehicle />}
                {waitingListFlowContext.activeStep === waitingListFlowContext.document.index && <DocumentUpload />}
                {waitingListFlowContext.activeStep === waitingListFlowContext.overview.index && <OrderOverview />}
                {waitingListFlowContext.activeStep === waitingListFlowContext.confirmation.index && (
                    <OrderConfirmation />
                )}
            </div>
        );
    };

    function init() {
        ReactDOM.render(
            <WaitingListFlowContextProvider appConfig={appConfig}>
                <App />
            </WaitingListFlowContextProvider>,
            appContainer
        );
    }

    return { init };
}

controller.add("waiting-list-flow-page", waitingListFlowPage);
